
const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
const audioCtx = new AudioCtx();

const playNote = (freq: number, type: OscillatorType, duration: number, volume: number = 0.1) => {
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, audioCtx.currentTime);

  gain.gain.setValueAtTime(volume, audioCtx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + duration);

  osc.connect(gain);
  gain.connect(audioCtx.destination);

  osc.start();
  osc.stop(audioCtx.currentTime + duration);
};

export const sounds = {
  roll: () => {
    // Short fast sequence of random beeps
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        playNote(400 + Math.random() * 600, 'square', 0.1, 0.05);
      }, i * 100);
    }
  },
  place: () => {
    // Solid "thud" sound
    playNote(150, 'sine', 0.2, 0.2);
    playNote(75, 'square', 0.1, 0.1);
  },
  destroy: () => {
    // Noise-like explosion
    const bufferSize = audioCtx.sampleRate * 0.2;
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const noise = audioCtx.createBufferSource();
    noise.buffer = buffer;
    const filter = audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1000, audioCtx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(100, audioCtx.currentTime + 0.2);

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.2, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.2);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(audioCtx.destination);
    noise.start();
  },
  win: () => {
    // Upward arpeggio
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      setTimeout(() => playNote(freq, 'square', 0.4, 0.1), i * 150);
    });
  },
  tick: () => {
    // Short high-pitched tick for turn timer
    playNote(1200, 'square', 0.08, 0.12);
  },
  tickUrgent: () => {
    // Urgent final tick (lower + louder)
    playNote(800, 'square', 0.15, 0.2);
    setTimeout(() => playNote(600, 'square', 0.15, 0.15), 80);
  },
  timeout: () => {
    // Time's up buzzer
    playNote(200, 'sawtooth', 0.3, 0.15);
    setTimeout(() => playNote(150, 'sawtooth', 0.3, 0.12), 150);
  },
  join: () => {
    // Two-tone chime: opponent joined the room
    playNote(660, 'sine', 0.15, 0.12);
    setTimeout(() => playNote(880, 'sine', 0.2, 0.12), 120);
  },
  lose: () => {
    // Downward slide
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(300, audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, audioCtx.currentTime + 1);
    gain.gain.setValueAtTime(0.1, audioCtx.currentTime);
    gain.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 1);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 1);
  },
  specialRoll: () => {
    // Fast arpeggio
    const notes = [440, 554.37, 659.25, 880, 1108.73, 1318.51, 1760];
    notes.forEach((freq, i) => {
      setTimeout(() => playNote(freq, 'square', 0.05, 0.08), i * 40);
    });
  },
  joker: () => {
    // Sparkly upward sweep
    const notes = [300, 400, 500, 600, 800, 1000, 1200, 1600, 2000];
    notes.forEach((freq, i) => {
      setTimeout(() => playNote(freq, 'sine', 0.1, 0.05), i * 30);
    });
  },
  shield: () => {
    // Metallic ding followed by low hum
    playNote(1200, 'sine', 0.1, 0.1);
    setTimeout(() => playNote(100, 'square', 0.8, 0.05), 100);
  },
  bomb: () => {
    // Short glitchy burst + falling pitch
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(800, audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(40, audioCtx.currentTime + 0.4);
    gain.gain.setValueAtTime(0.2, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.4);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.4);

    // Add noise layer
    for (let i = 0; i < 5; i++) {
      setTimeout(() => playNote(Math.random() * 500 + 100, 'square', 0.1, 0.1), i * 20);
    }
  },
  reroll: () => {
    // Click click + small fanfare
    playNote(2000, 'square', 0.02, 0.1);
    setTimeout(() => playNote(2200, 'square', 0.02, 0.1), 50);
    setTimeout(() => playNote(440, 'square', 0.1, 0.08), 150);
    setTimeout(() => playNote(554.37, 'square', 0.1, 0.08), 250);
    setTimeout(() => playNote(659.25, 'square', 0.3, 0.08), 350);
  }
};
